import { type ReactNode } from 'react';

import { Banner } from '@/components/Banner';
import { PLATFORM_GUIDES } from '@/lib/installGuides';
import type { SiteTechStack } from '@/types/auth';

/**
 * Tiny inline-markdown renderer for guide step strings:
 * `**bold**` becomes <strong>, `` `code` `` becomes <code>.
 */
function renderInline(text: string): ReactNode[] {
  const tokens = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return tokens.map((t, i) => {
    if (t.startsWith('**') && t.endsWith('**')) {
      return <strong key={i}>{t.slice(2, -2)}</strong>;
    }
    if (t.startsWith('`') && t.endsWith('`')) {
      return (
        <code
          key={i}
          className="rounded bg-muted px-1 py-0.5 text-[0.85em] font-mono"
        >
          {t.slice(1, -1)}
        </code>
      );
    }
    return <span key={i}>{t}</span>;
  });
}

interface InstallGuideProps {
  stack: SiteTechStack;
}

export function InstallGuide({ stack }: InstallGuideProps) {
  const guide = PLATFORM_GUIDES[stack];
  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">Install on {guide.title}</h2>
      <ol className="space-y-2 pl-5 text-sm [list-style:decimal]">
        {guide.steps.map((step, i) => (
          <li key={i} className="leading-relaxed">
            {renderInline(step)}
          </li>
        ))}
      </ol>
      <Banner variant="warn">
        <div className="space-y-1">
          <p>
            <strong className="font-semibold">Important: </strong>
            {renderInline(guide.important)}
            {guide.planLink && (
              <>
                {' '}
                <a
                  href={guide.planLink.href}
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2 hover:no-underline"
                >
                  {guide.planLink.label}
                </a>
              </>
            )}
          </p>
          {guide.afterImportant && (
            <p className="text-muted-foreground">
              {renderInline(guide.afterImportant)}
            </p>
          )}
        </div>
      </Banner>
    </div>
  );
}
